import React, { useState, useEffect, useRef } from "react";
import { UserFact, UserFactService } from "../api";
import ConfirmModal from "../components/ConfirmModal";
import Modal from "../components/Modal";

const PAGE_SIZE = 30;

const MemoriesPage: React.FC = () => {
  const [userFacts, setUserFacts] = useState<UserFact[]>([]);
  const [categories, setCategories] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingFact, setEditingFact] = useState<UserFact | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [formData, setFormData] = useState<Partial<UserFact>>({
    key: "",
    data: "",
    memory_type: "text",
    url: "",
    frequency_description: "",
  });
  const [pagination, setPagination] = useState({
    count: 0,
    next: null as string | null,
    previous: null as string | null,
    currentPage: 1,
    totalPages: 1,
  });

  // Modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteModalData, setDeleteModalData] = useState<{
    id: number;
    key: string;
  } | null>(null);
  const [isEmptyDataModal, setIsEmptyDataModal] = useState(false);

  const newRowRef = useRef<HTMLTableRowElement>(null);

  // Fetch user facts and categories on component mount
  useEffect(() => {
    fetchData();
  }, [showDeleted]);

  // Scroll new row into view when adding a new memory
  useEffect(() => {
    if (isAddingNew && newRowRef.current) {
      newRowRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [isAddingNew]);

  // Function to fetch data from API
  const fetchData = async (url?: string) => {
    setLoading(true);
    try {
      let factsData = {
        results: [] as UserFact[],
        count: 0,
        next: null as string | null,
        previous: null as string | null,
      };

      try {
        // If no URL is provided, request PAGE_SIZE items on initial load
        const initialUrl = url || `/api/memories/?limit=${PAGE_SIZE}`;
        factsData = await UserFactService.userFactsList(initialUrl, showDeleted);

        // Update user facts from results array
        setUserFacts(factsData.results);

        // Update pagination state
        setPagination({
          count: factsData.count,
          next: factsData.next,
          previous: factsData.previous,
          currentPage: url ? extractPageFromUrl(url) : 1,
          totalPages: Math.ceil(factsData.count / PAGE_SIZE),
        });
      } catch (factsError) {
        console.error("Error fetching user facts:", factsError);
      }

      let categoriesData: Record<string, string> = {};
      try {
        categoriesData = await UserFactService.userFactsCategories();
      } catch (categoriesError) {
        console.error("Error fetching categories:", categoriesError);
      }

      setCategories(categoriesData || {});

      // Clear error if we have data or if we just have no memories yet
      if (factsData.results.length > 0) {
        setError(null);
      } else if (Object.keys(categoriesData || {}).length > 0) {
        setError(null);
      } else {
        setError("Failed to load your memories. Please try again later.");
      }
    } catch (err) {
      console.error("Error in fetchData:", err);
      setError("Failed to load your memories. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to extract page number from URL
  const extractPageFromUrl = (url: string): number => {
    const match = url.match(/[?&]offset=(\d+)/);
    if (match && match[1]) {
      const offset = parseInt(match[1], 10);
      return Math.floor(offset / PAGE_SIZE) + 1;
    }
    return 1;
  };

  // Function to handle page change
  const handlePageChange = (page: number) => {
    const offset = (page - 1) * PAGE_SIZE;
    const baseUrl = `/api/memories/?offset=${offset}&limit=${PAGE_SIZE}`;
    fetchData(baseUrl);
  };

  // Function to render pagination controls
  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    return (
      <div className="pagination-container mt-3">
        <div className="pagination">
          <button
            className="pagination__button"
            disabled={!pagination.previous}
            onClick={() =>
              pagination.previous && handlePageChange(pagination.currentPage - 1)
            }
          >
            Previous
          </button>

          {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(number => (
            <button
              key={number}
              className={`pagination__number ${number === pagination.currentPage ? 'pagination__number--active' : ''}`}
              onClick={() => handlePageChange(number)}
            >
              {number}
            </button>
          ))}

          <button
            className="pagination__button"
            disabled={!pagination.next}
            onClick={() =>
              pagination.next && handlePageChange(pagination.currentPage + 1)
            }
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddNew = () => {
    setEditingFact(null);
    setIsAddingNew(true);
    setFormData({
      key: "",
      data: "",
      memory_type: "text",
      url: "",
      frequency_description: "",
    });
    setIsEditModalOpen(true);
  };

  // Function to open delete confirmation modal
  const openDeleteModal = (id: number, key: string) => {
    setDeleteModalData({ id, key });
    setIsDeleteModalOpen(true);
  };

  // Function to close delete confirmation modal
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeleteModalData(null);
  };

  // Function to handle the actual deletion
  const confirmDelete = async () => {
    if (!deleteModalData) return;

    setLoading(true);
    try {
      await UserFactService.userFactsDelete(deleteModalData.id.toString());
      fetchData();
    } catch (err) {
      console.error("Error deleting memory:", err);
      setError("Failed to delete memory. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Open empty data modal for confirmation
  const openEmptyDataModal = () => {
    setIsEmptyDataModal(true);
  };

  // Close empty data modal
  const closeEmptyDataModal = () => {
    setIsEmptyDataModal(false);
  };

  // Confirm empty data deletion
  const confirmEmptyDataDelete = async () => {
    if (!editingFact) return;

    setLoading(true);
    try {
      await UserFactService.userFactsDelete(String(editingFact.id));
      setEditingFact(null);
      fetchData();
    } catch (err) {
      console.error("Error deleting memory:", err);
      setError("Failed to delete memory. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (fact: UserFact) => {
    setEditingFact(fact);
    setFormData({
      key: fact.key,
      data: fact.data,
      memory_type: fact.memory_type,
      url: fact.url || "",
      frequency_description: fact.frequency_description || "",
    });
    setIsEditModalOpen(true);
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    // Validate that data is not blank
    if (!formData.data?.trim()) {
      if (editingFact) {
        openEmptyDataModal();
      } else {
        setError("Memory data cannot be blank. Please enter some data or cancel.");
      }
      return;
    }

    setLoading(true);
    try {
      if (editingFact?.id) {
        // Prepare request body with restore flag if needed
        const requestBody = { ...formData } as UserFact & { restore_deleted?: boolean };

        // Add restore flag if this is a deleted memory being restored
        const isRestoreOperation = editingFact.deleted_at;
        if (isRestoreOperation) {
          requestBody.restore_deleted = true;
        }

        await UserFactService.userFactsUpdate(
          String(editingFact.id),
          requestBody
        );

        // Optimistic update: if this was a restore operation and we're showing deleted memories,
        // remove the restored memory from the list
        if (isRestoreOperation && showDeleted) {
          setUserFacts(prevFacts => prevFacts.filter(fact => fact.id !== editingFact.id));
          // Update pagination count
          setPagination(prev => ({ ...prev, count: prev.count - 1 }));
        }
      } else {
        await UserFactService.userFactsCreate(formData as UserFact);
      }

      // Close modal and reset state
      setIsEditModalOpen(false);
      setEditingFact(null);
      setIsAddingNew(false);
      setFormData({
        key: "",
        data: "",
        memory_type: "text",
        url: "",
        frequency_description: "",
      });

      // For non-restore operations or when not showing deleted memories, fetch fresh data
      if (!editingFact?.deleted_at || !showDeleted) {
        fetchData();
      }
    } catch (err) {
      console.error("Error saving memory:", err);
      setError("Failed to save memory. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Replace handleDelete with openDeleteModal
  const handleDelete = (id: number, key: string) => {
    openDeleteModal(id, key);
  };

  // Add back the simplified renderMemoryRow function
  const renderMemoryRow = (fact: UserFact) => {
    // Truncate long data content for display in the table
    const truncateText = (text: string, maxLength = 100) => {
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    };

    const isDeleted = fact.deleted_at;

    return (
      <tr key={fact.id} className={`memories-table__row ${isDeleted ? 'memories-table__row--deleted' : ''}`}>
        <td>{fact.key}</td>
        <td title={fact.data}>{truncateText(fact.data)}</td>
        <td>{fact.memory_type_display}</td>
        <td>{fact.url ? (
          <a href={fact.url} target="_blank" rel="noopener noreferrer" className="memory-url">
            {truncateText(fact.url, 30)}
          </a>
        ) : "-"}</td>
        <td>{fact.frequency_description || "-"}</td>
        <td>
          <div className="action-buttons">
            <button
              className="button is-outlined"
              onClick={() => handleEdit(fact)}
            >
              {isDeleted ? "Restore" : "Edit"}
            </button>
            {!isDeleted && (
              <button
                className="button is-outlined is-danger"
                onClick={() => handleDelete(fact.id || 0, fact.key)}
              >
                Delete
              </button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const getModalTitle = () => {
    if (editingFact?.deleted_at) {
      return "Restore Memory";
    }
    return editingFact ? "Edit Memory" : "Add New Memory";
  };

  const getSubmitButtonText = () => {
    if (editingFact?.deleted_at) {
      return "Restore Memory";
    }
    return editingFact ? "Save Changes" : "Add Memory";
  };

  return (
    <div className="scientia-container">
      <div className="memories-header">
        <h1 className="title">Your Memories</h1>
        <p className="subtitle">
          Manage the information you want Scientia to remember about you.
        </p>
      </div>

      {error && (
        <div className="form-message is-error" role="alert">
          <button className="close-button" onClick={() => setError(null)}>×</button>
          {error}
        </div>
      )}

      {loading ? (
        <div className="loading-spinner-container">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <div className="scientia-card">
          <div className="scientia-card__content">
            <div className="memories-controls">
              <div className="show-deleted-toggle">
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={showDeleted}
                    onChange={(e) => setShowDeleted(e.target.checked)}
                  />
                  Show deleted memories
                </label>
              </div>
            </div>

            {userFacts.length === 0 ? (
              <div className="empty-state">
                <h3>{showDeleted ? "No deleted memories found" : "No memories found"}</h3>
                <p>
                  {showDeleted
                    ? "You haven't deleted any memories yet, or all deleted memories have been restored."
                    : "You haven't added any memories yet. Click \"Add New Memory\" to get started."
                  }
                </p>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="memories-table">
                    <thead>
                      <tr>
                        <th>Key</th>
                        <th>Data</th>
                        <th>Type</th>
                        <th>URL</th>
                        <th>Frequency</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userFacts.map((fact) => renderMemoryRow(fact))}
                    </tbody>
                  </table>
                </div>

                {renderPagination()}
              </>
            )}

            {!showDeleted && (
              <div className="add-memory-container">
                <button
                  className="button is-primary"
                  onClick={handleAddNew}
                >
                  Add New Memory
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmModal
        isActive={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        messages={[`Are you sure you want to delete the memory "${deleteModalData?.key}"?`, "This action cannot be undone."]}
        confirmText="Delete"
        cancelText="Cancel"
        isConfirmDanger={true}
      />

      <ConfirmModal
        isActive={isEmptyDataModal}
        onClose={closeEmptyDataModal}
        onConfirm={confirmEmptyDataDelete}
        title="Confirm Deletion"
        message="Saving with blank data will delete this memory. Are you sure you want to proceed?"
        confirmText="Delete"
        cancelText="Cancel"
        isConfirmDanger={true}
      />

      <Modal
        isActive={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingFact(null);
          setFormData({
            key: "",
            data: "",
            memory_type: "text",
            url: "",
            frequency_description: "",
          });
        }}
        title={getModalTitle()}
        size="medium"
      >
        <form onSubmit={handleSubmit}>
          {editingFact?.deleted_at && (
            <div className="deleted-memory-info">
              <div className="field">
                <label className="label">Deleted At</label>
                <div className="control">
                  <input
                    type="text"
                    className="input"
                    value={new Date(editingFact.deleted_at).toLocaleString()}
                    readOnly
                  />
                </div>
              </div>

              <div className="field">
                <label className="label">Deletion Reason</label>
                <div className="control">
                  <textarea
                    className="textarea"
                    value={editingFact.deleted_reason || "No reason provided"}
                    readOnly
                    rows={2}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="field">
            <label className="label">Key</label>
            <div className="control">
              <input
                name="key"
                required
                type="text"
                className="input"
                value={formData.key}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="field">
            <label className="label">Data</label>
            <div className="control">
              <textarea
                name="data"
                required
                rows={4}
                className="textarea"
                value={formData.data}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="field">
            <label className="label">Type</label>
            <div className="control">
              <div className="select is-fullwidth">
                <select
                  name="memory_type"
                  value={formData.memory_type}
                  onChange={handleInputChange}
                >
                  {Object.entries(categories).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="field">
            <label className="label">URL</label>
            <div className="control">
              <input
                name="url"
                type="text"
                className="input"
                placeholder="https://example.com"
                value={formData.url || ""}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="field">
            <label className="label">Frequency</label>
            <div className="control">
              <input
                name="frequency_description"
                type="text"
                className="input"
                placeholder="e.g., 'weekly'"
                value={formData.frequency_description || ""}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="field is-grouped is-grouped-right">
            <div className="control">
              <button
                type="button"
                className="button"
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingFact(null);
                  setFormData({
                    key: "",
                    data: "",
                    memory_type: "text",
                    url: "",
                    frequency_description: "",
                  });
                }}
              >
                Cancel
              </button>
            </div>
            <div className="control">
              <button
                type="submit"
                className="button is-primary"
                disabled={loading}
              >
                {getSubmitButtonText()}
              </button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default MemoriesPage;
